import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../components';
import { JobStatusBadge } from '../features/search/JobStatusBadge';
import { describeAvailableSearches } from '../features/search/capability-labels';
import { useCapabilities } from '../hooks/use-capabilities';
import { useSearchList } from '../hooks/use-searches';
import { describeError } from '../services/errors';
import { formatDateTime } from '../features/results/ResultCard';
import { PLATFORM_LABEL } from '../features/results/labels';

/** Quantas pesquisas o dashboard lista — e sobre quantas a contagem soma. */
const RECENT_LIMIT = 5;

export function DashboardPage() {
  const searches = useSearchList(RECENT_LIMIT);
  const recent = searches.data?.pages.flatMap((page) => page.data) ?? [];
  const totalResults = recent.reduce((sum, search) => sum + search.resultCount, 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Pesquisas recentes" value={String(recent.length)} />
        {/* A soma é só das pesquisas desta lista, e o rótulo precisa dizer isso
            (SPEC-014 RF-F21): "resultados coletados" sugeria o total. */}
        <StatCard
          label="Resultados nas pesquisas recentes"
          value={String(totalResults)}
          hint={`Soma das ${RECENT_LIMIT} pesquisas mais recentes`}
        />
        <AvailableSearchesCard />
      </div>

      <Card>
        <CardHeader
          title="Pesquisas recentes"
          description="As últimas consultas executadas por você."
          action={
            <Link to="/pesquisas/nova">
              <Button size="sm">Nova pesquisa</Button>
            </Link>
          }
        />

        {searches.isPending && <LoadingState label="Carregando pesquisas" />}

        {searches.isError && (
          <ErrorState
            message={describeError(searches.error)}
            onRetry={() => void searches.refetch()}
          />
        )}

        {!searches.isPending && !searches.isError && recent.length === 0 && (
          <EmptyState
            title="Nenhuma pesquisa ainda"
            description="Comece criando uma pesquisa por assunto ou por pessoa."
            action={
              <Link to="/pesquisas/nova">
                <Button size="sm">Criar a primeira pesquisa</Button>
              </Link>
            }
          />
        )}

        {recent.length > 0 && (
          <ul className="flex flex-col divide-y divide-border">
            {recent.map((search) => (
              <li key={search.id} className="py-3">
                <Link
                  to={`/pesquisas/${search.id}`}
                  className="flex flex-wrap items-center gap-3 rounded-md hover:bg-surface-raised"
                >
                  <span className="text-sm font-medium text-text">
                    {search.label ?? search.terms.join(', ') ?? 'Pesquisa'}
                  </span>
                  {search.platforms.map((platform) => (
                    <Badge key={platform}>{platform}</Badge>
                  ))}
                  <JobStatusBadge status={search.status} />
                  <span className="ml-auto text-xs text-text-muted">
                    {search.resultCount} resultado(s) · {formatDateTime(search.createdAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * O que cada plataforma aceita pesquisar agora (SPEC-014 §18, RF-F20).
 *
 * Substitui um "Modo de desenvolvimento" escrito à mão, que continuou na tela
 * depois de a coleta real ser ligada. Lê as capabilities, como o formulário: se
 * uma busca for ligada ou desligada por env, o card muda sem deploy de frontend.
 */
function AvailableSearchesCard() {
  const capabilities = useCapabilities();

  return (
    <Card className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-text-muted">Pesquisas disponíveis</span>
      {capabilities.isPending && <span className="text-sm text-text-muted">Carregando…</span>}
      {capabilities.isError && (
        <span className="text-sm text-text-muted">Não foi possível consultar agora.</span>
      )}
      {capabilities.data && (
        <ul className="flex flex-col gap-0.5">
          {capabilities.data.platforms.map((capability) => (
            <li key={capability.platform} className="text-sm text-text">
              <span className="font-semibold">{PLATFORM_LABEL[capability.platform]}:</span>{' '}
              {describeAvailableSearches(capability)}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-text-muted">{label}</span>
      <span className="text-xl font-semibold text-text">{value}</span>
      {hint && <span className="text-xs text-text-muted">{hint}</span>}
    </Card>
  );
}
